import { useState, useEffect, useCallback, useMemo } from 'react'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'
import { apiFetch } from '@/lib/api'
import { formatTick, CHART_COLORS } from './utils'
import { ChartPanel, ChartTooltip } from './components'

const axisProps = {
    stroke: 'var(--secondary-foreground)',
    tick: { fontSize: 11, fill: 'var(--secondary-foreground)' },
    tickLine: false,
    axisLine: { stroke: 'var(--border)' },
}

const rankColor = (rank) => {
    if (rank === 1) return 'var(--primary)'
    if (rank === 2) return 'var(--chart-4)'
    if (rank === 3) return 'var(--chart-3)'
    return 'var(--secondary)'
}

export default function UsersTab({ showId, range, tz }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const params = new URLSearchParams({ range, tz })
            const res = await apiFetch(`/api/shows/${showId}/statistics/users?${params.toString()}`)
            if (!res.ok) throw new Error('Failed to load user statistics')
            setData(await res.json())
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [showId, range, tz])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const resolution = data?.resolution || 'day'
    const series = data?.series || []
    const users = data?.users || []
    const leaderboard = data?.leaderboard || []

    // Stable color per user id; map user id -> display name for tooltip/legend.
    const { colorById, nameById } = useMemo(() => {
        const colorById = {}
        const nameById = {}
        users.forEach((u, i) => {
            colorById[u.userId] = CHART_COLORS[i % CHART_COLORS.length]
            nameById[u.userId] = u.name
        })
        return { colorById, nameById }
    }, [users])

    const maxTotal = leaderboard.length ? leaderboard[0].total : 0

    if (loading) {
        return <div className='py-20 text-center text-sm text-secondary-foreground/60'>Loading statistics...</div>
    }
    if (error) {
        return <div className='py-20 text-center text-sm text-destructive-foreground'>{error}</div>
    }

    const hasData = users.length > 0

    return (
        <div className='flex flex-col gap-4'>
            <ChartPanel title='Tasks submitted per user over time'>
                {hasData ? (
                    <div className='h-80 w-full'>
                        <ResponsiveContainer width='100%' height='100%'>
                            <BarChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
                                <XAxis dataKey='ts' tickFormatter={(ts) => formatTick(ts, resolution)} {...axisProps} />
                                <YAxis allowDecimals={false} {...axisProps} />
                                <Tooltip
                                    content={<ChartTooltip resolution={resolution} nameMap={nameById} sortByValue />}
                                    cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: 12 }}
                                    formatter={(value) => nameById[value] || value}
                                />
                                {users.map((u, i) => (
                                    <Bar
                                        key={u.userId}
                                        dataKey={u.userId}
                                        name={u.userId}
                                        stackId='users'
                                        fill={colorById[u.userId]}
                                        maxBarSize={32}
                                        radius={i === users.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <div className='py-16 text-center text-sm text-secondary-foreground/60'>
                        No task activity in this period.
                    </div>
                )}
            </ChartPanel>

            <ChartPanel title='Leaderboard'>
                {leaderboard.length ? (
                    <div className='flex flex-col gap-1'>
                        {leaderboard.map((entry) => (
                            <div
                                key={entry.userId}
                                className='flex items-center gap-3 rounded-md px-2 py-2 hover:bg-secondary/40'
                            >
                                <span
                                    className='flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-bold'
                                    style={{
                                        background: rankColor(entry.rank),
                                        color: entry.rank <= 3 ? 'var(--primary-foreground)' : 'var(--secondary-foreground)',
                                    }}
                                >
                                    {entry.rank}
                                </span>
                                <div className='flex flex-1 flex-col gap-1 min-w-0'>
                                    <div className='flex items-center justify-between gap-2'>
                                        <span className='truncate text-sm font-medium text-foreground'>{entry.name}</span>
                                        <span className='flex-none text-sm font-bold tabular-nums text-secondary-foreground'>
                                            {entry.total.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted'>
                                        <div
                                            className='h-full rounded-full'
                                            style={{
                                                width: `${maxTotal ? (entry.total / maxTotal) * 100 : 0}%`,
                                                background: colorById[entry.userId] || 'var(--primary)',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='py-16 text-center text-sm text-secondary-foreground/60'>
                        No task submissions in this period.
                    </div>
                )}
            </ChartPanel>
        </div>
    )
}
